import {
	FormulaCategory,
	ProductState,
	ProductType,
	Units,
} from 'src/app/app.enums';
import type { ICalorieObject } from './feed-object.model';

export class Product {
	calorieDensity: number;
	caloriesPerUnit: number;
	calorieObject!: ICalorieObject;
	displacementPerUnit: number;
	enforceScanning: boolean;
	containsHumanMilk: boolean;
	id: string;
	manufacturerName: string;
	manufacturerProductId: number;
	measuredUnit: Units;
	name: string;
	productCode: string;
	regexes: RegExp[];
	type: ProductType;
	additiveIncrement: number;
	defaultState: ProductState;

	// Attribute-derived fields (computed BE-side from ProductAttributes)
	isHmf: boolean;
	isBase: boolean;
	isAdditive: boolean;
	isRtf: boolean;
	restrictFromUrc: boolean;
	isFormula: boolean;
	isEbm: boolean;
	isDonor: boolean;
	isFortifier: boolean;
	mlToOzConversionRate: number | null;
	conversionRateSource: string | null;
	source: string | null;
	milkBottleType: string | null;
	formulaCategories: FormulaCategory[];
	displayUnitOfMeasure: string;
	/** Null for every product without a `prep-instructions` attribute — the vast majority. */
	prepInstructions: IProductPrepInstructions | null;

	// front end attributes
	hasUPC: boolean;

	// biome-ignore lint/suspicious/noExplicitAny:  allow additional fields on the model
	[propName: string]: any; // extra properties

	get displayName(): string {
		return this.name;
	}

	// biome-ignore lint/suspicious/noExplicitAny: allow additional fields on the model
	constructor(initial: any) {
		Object.keys(initial).forEach((key) => {
			if (key === 'regexes') {
				// this.regex = new RegExp(initial.regexes[0]);
				this.hasUPC = !!initial.regexes.find((r) => r.indexOf('upc') > -1);
				this.regexes = initial.regexes.map((r) => new RegExp(r));
			}
			if (key === 'code') {
				this.productCode = initial.code;
			}
			// biome-ignore lint/nursery/noSecrets: <explanation>
			if (key === 'volumeIncrementalConstraint') {
				this.additiveIncrement = initial.volumeIncrementalConstraint;
			} else {
				this[key] = initial[key];
			}
		});
	}
}

export interface IProduct {
	calorieDensity: number;
	caloriesPerUnit: number;
	code: string;
	containsHumanMilk: boolean;
	displacementPerUnit: number;
	enforceScanning: boolean;
	id: number;
	manufacturerName: string;
	manufacturerProductId: number;
	measuredUnit: Units;
	name: string;
	regexes: string[];
	type: ProductType;
	volumeIncrementalConstraint: number;
	defaultState: ProductState;

	// Attribute-derived fields (computed BE-side from ProductAttributes)
	isHmf: boolean;
	isBase: boolean;
	isAdditive: boolean;
	isRtf: boolean;
	restrictFromUrc: boolean;
	isFormula: boolean;
	isEbm: boolean;
	isDonor: boolean;
	isFortifier: boolean;
	mlToOzConversionRate: number | null;
	conversionRateSource: string | null;
	source: string | null;
	milkBottleType: string | null;
	formulaCategories: FormulaCategory[];

	// biome-ignore lint/suspicious/noExplicitAny: allow extra properties
	[propName: string]: any; // extra properties
}

/** The allowed product states, based on the Product.DefaultState.
 *
 * i.e., only products with a default state of frozen can be thawed
 */
export const ValidProductStates: Map<ProductState, ProductState[]> = new Map([
	[ProductState.Frozen, [ProductState.Frozen, ProductState.Thawed]],
	[ProductState.Stable, [ProductState.Stable, ProductState.Opened]],
]);

export interface IProductPrepInstructions {
    type: string;
	instructionDetails: ProductPrepInstructionDetails[];
}

export interface ProductPrepInstructionDetails {
    targetCaloricDensity: number
    caloricValue: number
    ratio: string;
    manufacturerMixingInstructions: string;
}
